import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom'
import { AuthProvider } from './auth/AuthContext'
import { initKeycloak } from './auth/keycloak'
import { loadRuntimeConfig } from './runtimeConfig'
import { OrganizationGate } from './components/OrganizationGate'
import { ThemeProvider } from './theme/ThemeProvider'
import { Shell } from './components/Shell'
import { Branding } from './screens/Branding'
import { CostCenterTree } from './screens/CostCenterTree'
import { ChartOfAccounts } from './screens/ChartOfAccounts'
import { CostDriverList } from './screens/CostDriverList'
import { DriverEditor } from './screens/DriverEditor'
import { AccountDriverMapping } from './screens/AccountDriverMapping'
import { RunConsole } from './screens/RunConsole'
import { ResultsExplorer } from './screens/ResultsExplorer'
import { ResultsLanding } from './screens/ResultsLanding'
import { QcDashboard } from './screens/QcDashboard'
import { AuditLog } from './screens/AuditLog'
import { Occupancy } from './screens/Occupancy'
import { Settings } from './screens/Settings'
import { ConfigurationOverview } from './screens/settings/ConfigurationOverview'
import { BedTypeSettings } from './screens/settings/BedTypeSettings'
import { ScheduleSettings } from './screens/settings/ScheduleSettings'
import { AlertSettings } from './screens/settings/AlertSettings'
import './index.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Configuration data changes only when someone edits it, and every mutation
      // invalidates explicitly. Refetching on window focus would make the tree flicker
      // every time an analyst tabs back from a spreadsheet.
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      retry: 1,
    },
  },
})

const router = createBrowserRouter([
  {
    path: '/',
    element: <Shell />,
    children: [
      // §10: the QC dashboard is the daily driver of the app — make it the default
      // landing page.
      { index: true, element: <Navigate to="/qc" replace /> },
      { path: 'qc', element: <QcDashboard /> },
      { path: 'cost-centers', element: <CostCenterTree /> },
      { path: 'accounts', element: <ChartOfAccounts /> },
      { path: 'drivers', element: <CostDriverList /> },
      { path: 'drivers/:id', element: <DriverEditor /> },
      { path: 'mapping', element: <AccountDriverMapping /> },
      { path: 'runs', element: <RunConsole /> },

      /*
          '/results' rendered <RunConsole /> — the same component as '/runs', so the Run
          Console and Results Explorer tabs showed one screen between them and only
          '/results/:runId' was ever the explorer. A placeholder from before there was
          anything to point at, which outlived its reason. See ResultsLanding.
      */
      { path: 'results', element: <ResultsLanding /> },
      { path: 'results/:runId', element: <ResultsExplorer /> },
      { path: 'occupancy', element: <Occupancy /> },
      { path: 'audit', element: <AuditLog /> },

      /*
          One path for everything configurable -- see Settings.tsx.

          These editors used to be scattered across the screens that consume them: bed
          types behind a mode toggle on Occupancy, schedules and alert recipients at the
          bottom of the Run Console, branding in the user menu. Each placement was a
          reasonable local decision and the aggregate had no answer to "where do I change
          things?", which is how a shipped bed-type editor got reported as missing.
      */
      {
        path: 'settings',
        element: <Settings />,
        children: [
          /*
              The overview is the index, not the first editor.

              Landing on a bed-type list answers a question nobody asked on the way in.
              Landing on what the configuration currently does — and what is wrong with
              it — is the thing worth seeing before touching any of it.
          */
          { index: true, element: <Navigate to="overview" replace /> },
          { path: 'overview', element: <ConfigurationOverview /> },
          { path: 'occupancy', element: <BedTypeSettings /> },
          { path: 'schedules', element: <ScheduleSettings /> },
          { path: 'alerts', element: <AlertSettings /> },
          { path: 'branding', element: <Branding /> },
        ],
      },

      // Branding moved under /settings. Kept as a redirect rather than removed: the old
      // path is in bookmarks and in the runbook, and a dead link is a worse answer than
      // a redirect that costs one line.
      { path: 'branding', element: <Navigate to="/settings/branding" replace /> },
    ],
  },
])

/*
  Authenticate BEFORE rendering.

  keycloak-js redirects to the login page during init when the session is absent, so an
  unauthenticated user never reaches React at all — there is no flash of application
  chrome, and no window in which a component could fire an unauthenticated request.
*/
async function bootstrap() {
  /*
      Before anything else, including Keycloak.

      This is what makes one build deployable to any tenant: the identity provider's URL,
      realm and client id come from /config.json rather than from constants baked in at
      build time. The Keycloak client cannot be constructed without them, so nothing may
      touch it until this resolves. A missing file is fine and normal — the values the
      bundle was built with are a complete configuration on their own.
  */
  await loadRuntimeConfig()

  try {
    await initKeycloak()
  } catch (error) {
    /*
        The one screen in the product that is deliberately unbranded, and the hard-coded
        colours below are correct.

        Reaching here means the identity provider is unreachable, so there is no token, no
        organization, and therefore no theme to read — the stylesheet has not even been
        imported yet at this point in the module graph. An error page that waited for a
        brand it can never get would be a blank page instead.
    */
    document.getElementById('root')!.innerHTML = `
      <div style="display:flex;height:100%;align-items:center;justify-content:center;
                  font-family:system-ui;color:#B8231B;padding:24px;text-align:center">
        <div>
          <p style="font-weight:600;margin:0 0 6px">Could not reach the identity provider.</p>
          <p style="margin:0;color:#5A6873;font-size:13px">${String(error)}</p>
        </div>
      </div>`
    return
  }

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          {/*
              Above the organization gate, not below it. The gate carries the lockup and
              the product's name, and for the great majority of users — the ones who
              belong to exactly one organization — that organization is already resolved
              by the time it renders. Below the gate, those users would see Tracer's navy
              for one frame before their own brand arrived.
          */}
          <ThemeProvider>
            <OrganizationGate>
              <RouterProvider router={router} />
            </OrganizationGate>
          </ThemeProvider>
        </AuthProvider>
      </QueryClientProvider>
    </StrictMode>,
  )
}

void bootstrap()
